import { useCurrency } from '../../context/CurrencyContext';
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import {
  Plus, X, Upload, Download, Search, Eye, Edit2,
  Store, MapPin, Phone, Mail, Users, Check, Trash2
} from 'lucide-react';
import api from '../../api/client';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const statusColors = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  inactive: 'bg-gray-100 text-gray-500 border-gray-200',
  pilot: 'bg-blue-50 text-blue-600 border-blue-200',
};

const CATEGORIES = ['Fashion', 'F&B', 'Cinema', 'Electronics', 'Jewellery', 'Health & Beauty', 'Sports', 'Home & Living', 'Services', 'Other'];

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
      <div className={`bg-white rounded-2xl w-full ${wide ? 'max-w-2xl' : 'max-w-md'} shadow-2xl my-4`}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="font-heading text-lg font-bold text-[#561D1A]">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-5 max-h-[75vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

export default function StoresTab({ onViewDetail }) {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editStore, setEditStore] = useState(null);
  const [form, setForm] = useState({
    name: '', description: '', category: '', logo_url: '',
    location_floor: '', location_unit: '',
    contact_person: '', contact_email: '', contact_phone: '',
    settlement_frequency: 'monthly', commission_percent: 0,
  });
  const [saving, setSaving] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const logoInputRef = useRef(null);
  const csvInputRef = useRef(null);

  const load = () => {
    api.get('/api/store/admin/stores')
      .then(res => setStores(res.data))
      .catch(() => toast.error('Failed to load stores'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditStore(null);
    setForm({
      name: '', description: '', category: '', logo_url: '',
      location_floor: '', location_unit: '',
      contact_person: '', contact_email: '', contact_phone: '',
      settlement_frequency: 'monthly', commission_percent: 0,
    });
    setModalOpen(true);
  };

  const openEdit = (s) => {
    setEditStore(s);
    setForm({
      name: s.name || '', description: s.description || '', category: s.category || '',
      logo_url: s.logo_url || '',
      location_floor: s.location_floor || '', location_unit: s.location_unit || '',
      contact_person: s.contact_person || '', contact_email: s.contact_email || '',
      contact_phone: s.contact_phone || '',
      settlement_frequency: s.settlement_frequency || 'monthly',
      commission_percent: s.commission_percent || 0,
      status: s.status || (s.is_active ? 'active' : 'inactive'),
    });
    setModalOpen(true);
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/api/config/admin/upload', fd);
      setForm(f => ({ ...f, logo_url: res.data.url }));
      toast.success('Logo uploaded');
    } catch {
      toast.error('Upload failed');
    } finally { setUploadingLogo(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      payload.commission_percent = parseFloat(payload.commission_percent) || 0;
      if (editStore) {
        await api.put(`/api/store/admin/stores/${editStore.id}`, payload);
        toast.success('Store updated');
      } else {
        await api.post('/api/store/admin/stores', payload);
        toast.success('Store created');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(err.displayMessage || 'Failed to save');
    } finally { setSaving(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/api/store/admin/stores/csv-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'stores_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch { toast.error('Download failed'); }
  };

  const handleCsvImport = async (file) => {
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/api/store/admin/stores/bulk-import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${res.data.created} store(s) imported`);
      if (res.data.errors?.length) {
        res.data.errors.forEach(e => toast.error(e));
      }
      setImportModal(false);
      load();
    } catch (err) {
      toast.error(err.displayMessage || 'Import failed');
    } finally { setImporting(false); }
  };

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(prev => prev.size === filtered.length ? new Set() : new Set(filtered.map(s => s.id)));
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this store permanently?')) return;
    try { await api.delete(`/api/store/admin/stores/${id}`); toast.success('Store deleted'); load(); setSelected(prev => { const n = new Set(prev); n.delete(id); return n; }); }
    catch { toast.error('Delete failed'); }
  };
  const handleBulkDelete = async () => {
    if (!selected.size || !window.confirm(`Delete ${selected.size} store(s) permanently?`)) return;
    setDeleting(true);
    try { const res = await api.post('/api/store/admin/stores/bulk-delete', { ids: [...selected] }); toast.success(`${res.data.deleted} store(s) deleted`); setSelected(new Set()); load(); }
    catch { toast.error('Bulk delete failed'); }
    finally { setDeleting(false); }
  };

  const filtered = stores.filter(s =>
    !searchTerm || s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.category || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const { formatDateTime: formatDate } = useCurrency();

  return (
    <div data-testid="stores-tab">
      {/* Top Actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3 flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2 bg-white flex-1 max-w-sm">
            <Search size={14} className="text-gray-400" />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search stores..." className="outline-none text-sm text-gray-700 w-full bg-transparent"
              data-testid="search-stores" />
          </div>
          <span className="text-xs text-gray-400">{filtered.length} store(s)</span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setImportModal(true)}
            className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-xl text-xs font-medium transition-colors"
            data-testid="import-csv-btn">
            <Upload size={13} /> Import CSV
          </button>
          <button onClick={openCreate}
            className="flex items-center gap-1.5 bg-[#EC1A23] text-white px-4 py-2 rounded-xl text-xs font-medium hover:bg-[#C4121A] transition-colors"
            data-testid="add-store-btn">
            <Plus size={13} /> Add Store
          </button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-3 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5" data-testid="bulk-action-bar">
          <span className="text-sm text-red-700 font-medium">{selected.size} selected</span>
          <button onClick={handleBulkDelete} disabled={deleting}
            className="flex items-center gap-1.5 text-xs bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
            data-testid="bulk-delete-btn">
            <Trash2 size={12} /> {deleting ? 'Deleting...' : 'Delete Selected'}
          </button>
          <button onClick={() => setSelected(new Set())} className="text-xs text-gray-500 hover:text-gray-700 ml-auto">Clear</button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-gray-50/80 border-b border-gray-100">
              <tr>
                <th className="px-3 py-3 w-10">
                  <input type="checkbox" checked={selected.size > 0 && selected.size === filtered.length}
                    onChange={toggleAll} className="rounded border-gray-300 accent-[#EC1A23]" data-testid="select-all-stores" />
                </th>
                {['Store', 'Category', 'Location', 'Status', 'Settlement', 'Last Settlement', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-16 text-gray-400">
                  <Store size={32} className="mx-auto mb-2 text-gray-300" />
                  <p>No stores found</p>
                </td></tr>
              ) : filtered.map(s => (
                <tr key={s.id} className={`hover:bg-gray-50/60 cursor-pointer transition-colors ${selected.has(s.id) ? 'bg-red-50/30' : ''}`}
                  onClick={() => onViewDetail?.(s.id)} data-testid="store-row">
                  <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleSelect(s.id)}
                      className="rounded border-gray-300 accent-[#EC1A23]" data-testid="select-store" />
                  </td>                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {s.logo_url ? (
                        <img src={s.logo_url.startsWith('http') ? s.logo_url : `${API_BASE}${s.logo_url}`}
                          alt="" className="w-8 h-8 rounded-lg object-cover border border-gray-100" />
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-[#561D1A]/10 flex items-center justify-center">
                          <Store size={14} className="text-[#561D1A]" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-800 text-sm">{s.name}</p>
                        {s.contact_person && <p className="text-[10px] text-gray-400">{s.contact_person}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{s.category || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {s.location_floor || s.location_unit ? (
                      <span className="flex items-center gap-1"><MapPin size={10} /> {[s.location_floor, s.location_unit].filter(Boolean).join(', ')}</span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2.5 py-1 rounded-full font-semibold border ${statusColors[s.status || (s.is_active ? 'active' : 'inactive')]}`}>
                      {(s.status || (s.is_active ? 'Active' : 'Inactive')).charAt(0).toUpperCase() + (s.status || (s.is_active ? 'active' : 'inactive')).slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 capitalize">{s.settlement_frequency || 'monthly'}</td>
                  <td className="px-4 py-3 text-xs text-gray-400">{formatDate(s.last_settlement_date)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><Users size={10} /> {s.user_count || 0}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <button onClick={() => onViewDetail?.(s.id)}
                        className="text-xs text-[#561D1A] hover:text-[#EC1A23] flex items-center gap-1 font-medium px-2 py-1 rounded-lg hover:bg-gray-50"
                        data-testid="view-store-btn">
                        <Eye size={12} /> View
                      </button>
                      <button onClick={() => openEdit(s)}
                        className="text-xs text-gray-400 hover:text-[#561D1A] p-1 rounded-lg hover:bg-gray-50"
                        data-testid="edit-store-btn">
                        <Edit2 size={12} />
                      </button>
                      <button onClick={() => handleDelete(s.id)}
                        className="text-xs text-gray-300 hover:text-red-500 p-1 rounded-lg hover:bg-red-50 transition-colors"
                        data-testid="delete-store-btn">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Store Modal */}
      {modalOpen && (
        <Modal title={editStore ? 'Edit Store' : 'Add Store'} onClose={() => setModalOpen(false)} wide>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Store Name *</label>
                <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="store-name-input" />
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Category</label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="store-category-select">
                  <option value="">Select category...</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {editStore && (
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Status</label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="store-status-select">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pilot">Pilot</option>
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Floor</label>
                <input value={form.location_floor} onChange={e => setForm(f => ({ ...f, location_floor: e.target.value }))}
                  placeholder="e.g. Ground Floor" className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" />
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Unit</label>
                <input value={form.location_unit} onChange={e => setForm(f => ({ ...f, location_unit: e.target.value }))}
                  placeholder="e.g. G-12" className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" />
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-3">Contact Information</p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Contact Person</label>
                  <input value={form.contact_person} onChange={e => setForm(f => ({ ...f, contact_person: e.target.value }))}
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Email</label>
                  <input type="email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))}
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Phone</label>
                  <input value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))}
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-3">Settlement Profile</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Frequency</label>
                  <select value={form.settlement_frequency} onChange={e => setForm(f => ({ ...f, settlement_frequency: e.target.value }))}
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent">
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Commission %</label>
                  <input type="number" step="0.1" min="0" max="100" value={form.commission_percent}
                    onChange={e => setForm(f => ({ ...f, commission_percent: e.target.value }))}
                    className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" />
                </div>
              </div>
            </div>

            {/* Logo Upload */}
            <div className="border-t border-gray-100 pt-4">
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-2">Store Logo</label>
              {form.logo_url && (
                <div className="mb-2 relative inline-block">
                  <img src={form.logo_url.startsWith('http') ? form.logo_url : `${API_BASE}${form.logo_url}`}
                    alt="Logo" className="h-16 rounded-lg object-cover border" />
                  <button type="button" onClick={() => setForm(f => ({ ...f, logo_url: '' }))}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center"><X size={10} /></button>
                </div>
              )}
              <input type="file" ref={logoInputRef} accept="image/*" onChange={e => handleLogoUpload(e.target.files[0])} className="hidden" />
              <button type="button" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}
                className="flex items-center gap-2 text-xs border border-dashed border-gray-300 text-gray-500 hover:border-[#561D1A] px-3 py-2 rounded-lg"
                data-testid="upload-logo-btn">
                {uploadingLogo ? 'Uploading...' : <><Upload size={12} /> Upload Logo</>}
              </button>
            </div>

            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2} className="w-full border-2 border-gray-200 focus:border-[#561D1A] p-2 text-sm outline-none rounded-xl bg-transparent resize-none" />
            </div>

            <button type="submit" disabled={saving} className="w-full bg-[#EC1A23] hover:bg-[#C4121A] disabled:opacity-60 text-white py-3 rounded-full text-sm font-medium" data-testid="save-store-btn">
              {saving ? 'Saving...' : editStore ? 'Update Store' : 'Create Store'}
            </button>
          </form>
        </Modal>
      )}

      {/* Import CSV Modal */}
      {importModal && (
        <Modal title="Bulk Import Stores" onClose={() => setImportModal(false)}>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Upload a CSV file to import multiple stores at once.</p>
            <button onClick={downloadTemplate}
              className="flex items-center gap-2 text-xs text-[#561D1A] border border-[#561D1A]/30 px-3 py-2 rounded-lg hover:bg-[#561D1A]/5"
              data-testid="download-template-btn">
              <Download size={13} /> Download CSV Template
            </button>
            <div className="border-t border-gray-100 pt-4">
              <input type="file" ref={csvInputRef} accept=".csv" onChange={e => handleCsvImport(e.target.files[0])} className="hidden" />
              <button onClick={() => csvInputRef.current?.click()} disabled={importing}
                className="w-full flex items-center justify-center gap-2 bg-[#EC1A23] text-white py-3 rounded-full text-sm font-medium hover:bg-[#C4121A] disabled:opacity-50"
                data-testid="upload-csv-btn">
                <Upload size={14} /> {importing ? 'Importing...' : 'Select & Upload CSV'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
